import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PatientOfferNation } from '../../services/api';
import './OfferPopover.css';

interface OfferPopoverProps {
  offers: PatientOfferNation[];
  children: React.ReactNode;
}

function OfferPopover({ offers, children }: OfferPopoverProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; flipUp: boolean; flipLeft: boolean }>({ top: 0, left: 0, flipUp: false, flipLeft: false });
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Calculate fixed position based on trigger's viewport coordinates
  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const panelHeight = panelRef.current?.offsetHeight || 200;
    const viewportHeight = window.innerHeight;

    const panelWidth = panelRef.current?.offsetWidth || 220;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - triggerRect.bottom;
    const flipUp = spaceBelow < panelHeight && triggerRect.top > panelHeight;

    const spaceRight = viewportWidth - triggerRect.left;
    const flipLeft = spaceRight < panelWidth;

    setPosition({
      top: flipUp ? triggerRect.top : triggerRect.bottom + 4,
      left: flipLeft ? triggerRect.right : triggerRect.left,
      flipUp,
      flipLeft
    });
  }, []);

  // Position on open + reposition on scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePosition();

    // Listen for scroll on any ancestor (captures table-scroll-area scroll)
    const handleScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, updatePosition]);

  // Reposition after panel renders (to get accurate height for flip)
  useEffect(() => {
    if (open && panelRef.current) {
      updatePosition();
    }
  }, [open, updatePosition]);

  const getOfferTypeIcon = (offer: PatientOfferNation) => {
    const offerType = offer.offerType;
    switch (offerType) {
      case 'TREATMENT_AND_TRANSPORT':
      case 'BOTH':
        return <img src="/icons/offer_combined_gruen.webp" alt="Full Offer" width={28} height={16} />;
      case 'TREATMENT_ONLY':
      case 'TREATMENT':
        // If ext. transport exists, show yellow truck; otherwise empty truck
        if (offer.transportNation && offer.transportNation !== offer.nation) {
          return (
            <>
              <img src="/icons/delivery_gelb.webp" alt="Ext. Transport" width={14} height={14} />
              <img src="/icons/hospital_gruen.webp" alt="Treatment" width={14} height={14} />
            </>
          );
        }
        return (
          <>
            <img src="/icons/delivery_leer.webp" alt="No Transport" width={14} height={14} />
            <img src="/icons/hospital_gruen.webp" alt="Treatment" width={14} height={14} />
          </>
        );
      case 'TRANSPORT_EXTENSION':
      case 'TRANSPORT':
        return (
          <>
            <img src="/icons/delivery_gruen.webp" alt="Transport" width={14} height={14} />
            <img src="/icons/hospital_gelb.webp" alt="Other Treatment" width={14} height={14} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="offer-popover-wrapper" ref={ref}>
      <div
        className="offer-popover-trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (offers.length > 0) setOpen(!open);
        }}
        style={{ cursor: offers.length > 0 ? 'pointer' : 'default' }}
      >
        {children}
      </div>
      {open && offers.length > 0 && (
        <div
          ref={panelRef}
          className="offer-popover-panel"
          style={{
            position: 'fixed',
            top: position.flipUp ? 'auto' : `${position.top}px`,
            bottom: position.flipUp ? `${window.innerHeight - position.top + 4}px` : 'auto',
            left: position.flipLeft ? 'auto' : `${position.left}px`,
            right: position.flipLeft ? `${window.innerWidth - position.left}px` : 'auto'
          }}
        >
          <div className="offer-popover-header">
            Offers ({offers.length})
          </div>
          <div className="offer-popover-list">
            {offers.map((o, i) => (
              <div key={i} className="offer-popover-row">
                <span className="offer-popover-nation">
                  {o.flagImage && (
                    <img src={o.flagImage} alt={o.nation} className="offer-popover-flag" />
                  )}
                  {o.nation}
                </span>
                <span className="offer-popover-type">
                  {getOfferTypeIcon(o)}
                  <span className="offer-popover-label">{o.label}</span>
                </span>
                {o.transportNation && o.transportNation !== o.nation && (
                  <span className="offer-popover-transport" title={`Transport: ${o.transportNation}`}>
                    Transport: {o.transportNation}
                  </span>
                )}
                {o.hubName && (
                  <span className="offer-popover-hub" title={o.hubName}>
                    {o.hubName}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OfferPopover;
